import React, { useEffect } from "react";

/**
 * ChatBubble
 * Props:
 *  - role: "user" | "assistant" | "system"
 *  - text: string
 *  - typing?: boolean
 *  - timestamp?: Date
 *  - meta?: { followup_questions?: string[] }
 *  - onChipClick?: function
 */
export default function ChatBubble({
  role = "assistant",
  text = "",
  typing = false,
  timestamp,
  meta = {},
  onChipClick,
}) {
  const isUser = role === "user";

  // ------------------------------------------------------------------
  // Detect language from message text (UI-level, safe & lightweight)
  // ------------------------------------------------------------------
  const detectLanguage = (text) => {
    if (!text) return "en";
    for (const ch of text) {
      const code = ch.charCodeAt(0);
      // Devanagari (Hindi)
      if (code >= 0x0900 && code <= 0x097f) return "hi";
    }
    return "en";
  };

  const language = detectLanguage(text);

  // ------------------------------------------------------------------
  // Language-aware UI labels (Phase-1)
  // ------------------------------------------------------------------
  const UI_TEXT = {
    en: {
      typing: "AI is typing…",
      listen: "🔊 Listen",
    },
    hi: {
      typing: "AI टाइप कर रहा है…",
      listen: "🔊 सुनें",
    },
  };

  const ui = UI_TEXT[language] || UI_TEXT.en;

  // ------------------------------------------------------------------
  // Ensure voices are loaded (CRITICAL for Indian voices)
  // ------------------------------------------------------------------
  useEffect(() => {
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.onvoiceschanged = () => {
        window.speechSynthesis.getVoices();
      };
    }
  }, []);

  // ------------------------------------------------------------------
  // Speak assistant message (Indian Female Voice)
  // ------------------------------------------------------------------
  const speakText = () => {
    if (!text || !window.speechSynthesis) return;

    const synth = window.speechSynthesis;
    synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    const voices = synth.getVoices();

    // 🎯 Prefer Indian Female voices
    const preferredVoice =
      voices.find((v) => v.name.includes("Kalpana")) || // HI-IN Female
      voices.find((v) => v.name.includes("Heera")) ||   // EN-IN Female
      voices.find((v) => v.name.includes("Swara")) ||   // EN-IN Female
      voices.find((v) => v.lang === "hi-IN") ||
      voices.find((v) => v.lang === "en-IN") ||
      voices.find((v) => v.lang.startsWith("en")) ||
      voices[0];

    if (preferredVoice) {
      utterance.voice = preferredVoice;
      utterance.lang = preferredVoice.lang;
    }

    // 🎚️ Natural human-like tone
    utterance.rate = 0.95;
    utterance.pitch = 1.05;
    utterance.volume = 1;

    synth.speak(utterance);
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        marginBottom: "10px",
      }}
    >
      <div style={bubbleStyle(isUser)}>
        {/* Typing indicator */}
        {typing ? (
          <div style={styles.typing}>{ui.typing}</div>
        ) : (
          <>
            {/* Message text */}
            <div
              style={styles.messageText}
              dangerouslySetInnerHTML={{
                __html: formatText(text),
              }}
            />

            {/* 🔊 Speak button (assistant only) */}
            {!isUser && text && (
              <div style={styles.audioRow}>
                <button
                  style={styles.speakButton}
                  onClick={speakText}
                  title={ui.listen}
                >
                  {ui.listen}
                </button>
              </div>
            )}

            {/* Follow-up question chips */}
            {meta?.followup_questions?.length > 0 && (
              <div style={styles.chips}>
                {meta.followup_questions.map((q, i) => (
                  <button
                    key={i}
                    style={styles.chip}
                    onClick={() => onChipClick?.(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Timestamp */}
            {timestamp && (
              <div style={styles.time}>
                {new Date(timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Helpers */
/* ------------------------------------------------------------------ */

function formatText(text) {
  if (!text) return "";
  return text.replace(/\n\n/g, "<br/><br/>").replace(/\n/g, "<br/>");
}

/* ------------------------------------------------------------------ */
/* Styles */
/* ------------------------------------------------------------------ */

const bubbleStyle = (isUser) => ({
  maxWidth: "72%",
  padding: "12px 14px",
  borderRadius: "16px",
  backgroundColor: isUser ? "#2563eb" : "#f9fafb",
  color: isUser ? "#ffffff" : "#111827",
  fontSize: "14px",
  lineHeight: "1.6",
  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
  borderTopRightRadius: isUser ? "4px" : "16px",
  borderTopLeftRadius: isUser ? "16px" : "4px",
});

const styles = {
  messageText: {
    whiteSpace: "normal",
  },

  typing: {
    fontStyle: "italic",
    color: "#6b7280",
    fontSize: "13px",
  },

  audioRow: {
    marginTop: "6px",
    display: "flex",
  },

  speakButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    fontSize: "13px",
    cursor: "pointer",
    padding: 0,
    fontWeight: 500,
  },

  chips: {
    marginTop: "10px",
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },

  chip: {
    fontSize: "12px",
    padding: "5px 12px",
    borderRadius: "999px",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#374151",
    cursor: "pointer",
  },

  time: {
    marginTop: "6px",
    fontSize: "11px",
    color: "#9ca3af",
    textAlign: "right",
  },
};
